/**
 * The exercise creator — KANON `Cwiczenia` 17.09.2026, variant **g · Trzy kroki**. A rail that
 * teaches on the left, one step on the right, and a footer with one ember primary.
 *
 * Composition only. `lib/useCreateExercise` owns the draft, the step and the save; every decision
 * it composes lives in `model/`.
 *
 * ── `Dialog`, not `AlertDialog` ─────────────────────────────────────────────────────────────
 * Nothing is written until the primary is pressed, so Escape and the scrim both dismiss — the same
 * call `features/create-plan` makes. There is no discard confirm: the phone asks one
 * (`createWizard.discard.*`) and the canon draws none, and a confirm the canon does not draw is a
 * design this build does not invent.
 *
 * ── The popup is rendered UNCONDITIONALLY ───────────────────────────────────────────────────
 * Base UI decides a closed dialog has finished by waiting on the animations of whatever `popupRef`
 * resolves to, and that wait never calls back when the ref is `null`. A popup rendered only while a
 * request existed would leave its backdrop on the page for ever — the defect the theatre met in
 * PR 1. So the popup always stands, and the request it draws OUTLIVES `open` (the caller holds it):
 * the contents close with the dialog rather than a frame before it.
 *
 * ── ONE size on every step ──────────────────────────────────────────────────────────────────
 * Owner decision D11: 1180 × 820, so „Dalej" never moves. The step body scrolls inside `.content`;
 * the rail and the footer stand. At 1280 × 620 the box is capped by the window and the same
 * scroller takes the difference — fit or scroll, never clip.
 *
 * ── The step is announced by MOVING FOCUS to its heading ────────────────────────────────────
 * `CreatePlanDialog`'s pattern: on open and on every step change the heading takes focus, which
 * announces the step, puts the next Tab at the top of it, and leaves a visible marker. The heading
 * is the `Dialog.Title`, so the dialog's accessible name is the step the trainer is on.
 *
 * ── The contents are KEYED by the request ───────────────────────────────────────────────────
 * A second opening — the same movement again, or another one — starts from its own draft. The key
 * sits on the contents rather than on the popup: a keyed popup is closed and reopened by Base UI
 * (the theatre recorded it), which drops the keypress that arrives in between.
 */

import { useEffect, useId, useRef, useState, type ComponentProps } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import { useTranslation } from 'react-i18next';

import type { ExerciseDetail } from '~/entities/exercise-library';

import type { SaveExerciseResult } from '../api/exerciseMutations';
import { useCreateExercise, type CreateExerciseMode, type ProposedNewExercise } from '../lib/useCreateExercise';

import { StepBasics } from './StepBasics';
import { StepBody } from './StepBody';
import { StepMaterials } from './StepMaterials';
import { WizardFooter } from './WizardFooter';
import { WizardRail } from './WizardRail';
import styles from './CreateExerciseDialog.module.css';

/** What the wizard was opened to do, on which movement. */
export interface ExerciseCreatorRequest {
  readonly mode: CreateExerciseMode;
  /** The movement being edited or copied. `null` for a new one. */
  readonly source: ExerciseDetail | null;
  /** A fresh number per opening, so two openings on one movement start from two drafts. */
  readonly opening: number;
  /**
   * A NEW movement for a name Buddy wrote (ai-plan-builder R8, „Zweryfikuj i dodaj"): the name, and
   * Buddy's proposal to fill the steps from. The mode stays `create` and the title „Nowe ćwiczenie";
   * the save marks the movement for review. Ignored with a source.
   */
  readonly proposed?: ProposedNewExercise;
}

export interface CreateExerciseDialogProps {
  readonly open: boolean;
  /** The request being drawn. Held by the caller PAST `open` — see the header. */
  readonly request: ExerciseCreatorRequest | null;
  readonly onClose: () => void;
  /** The save landed and the catalogue has been re-read. */
  readonly onSaved: (result: SaveExerciseResult, request: ExerciseCreatorRequest) => void;
  /** Where focus goes on close. Omitted, Base UI returns it to whatever opened the dialog. */
  readonly finalFocus?: ComponentProps<typeof Dialog.Popup>['finalFocus'];
}

export function CreateExerciseDialog({
  open,
  request,
  onClose,
  onSaved,
  finalFocus,
}: CreateExerciseDialogProps) {
  const heading = useRef<HTMLHeadingElement>(null);
  const modeId = useId();
  // The save lives in the wizard below, which reports it here the moment it starts.
  const [saving, setSaving] = useState(false);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // Escape and the scrim are refused while a save is in flight — the same rule
        // `ConfirmDialog` keeps when busy. Closing over a running save would leave its answer
        // arriving into a dialog the trainer believes they abandoned.
        if (!next && !saving) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={styles.backdrop} />
        <Dialog.Popup
          className={styles.popup}
          initialFocus={heading}
          finalFocus={finalFocus}
          // The rail's mode title — „Nowe ćwiczenie", „Edytuj ćwiczenie", „Duplikuj ćwiczenie" — so
          // the three are told apart; the NAME is the step, which is the same in all three.
          aria-describedby={request === null ? undefined : modeId}
          aria-busy={saving}
        >
          {request !== null && (
            <Wizard
              key={`${request.mode}-${String(request.source?.id ?? 0)}-${String(request.opening)}`}
              request={request}
              heading={heading}
              modeId={modeId}
              onBusyChange={setSaving}
              onClose={onClose}
              onSaved={onSaved}
            />
          )}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Wizard({
  request,
  heading,
  modeId,
  onBusyChange,
  onClose,
  onSaved,
}: {
  readonly onBusyChange: (busy: boolean) => void;
  readonly request: ExerciseCreatorRequest;
  readonly heading: React.RefObject<HTMLHeadingElement | null>;
  readonly modeId: string;
  readonly onClose: () => void;
  readonly onSaved: CreateExerciseDialogProps['onSaved'];
}) {
  const { t } = useTranslation();
  const wizard = useCreateExercise({
    mode: request.mode,
    source: request.source,
    ...(request.proposed === undefined ? {} : { proposed: request.proposed }),
    onSaved: (result) => {
      onSaved(result, request);
    },
    onBusyChange,
  });

  // Announce the step by moving focus to its heading. See the header.
  useEffect(() => {
    heading.current?.focus();
  }, [wizard.step, heading]);

  // A refused „Dalej”, rail jump or save: focus goes to the field that fixes it, AFTER the heading
  // effect above, so on a step change the field is where focus ends.
  const content = useRef<HTMLDivElement>(null);
  const { focusRequest } = wizard;
  useEffect(() => {
    if (focusRequest === null) return;
    content.current
      ?.querySelector<HTMLElement>(`[data-blocker="${focusRequest.blocker}"]`)
      ?.focus();
  }, [focusRequest]);

  return (
    <>
      <WizardRail wizard={wizard} titleId={modeId} />
      <div className={styles.body}>
        {/* `inert` while a save runs: it sends the draft as it stood at the press, so nothing here
          * may change under it (`useCreateExercise`). The footer stays live and says why. */}
        <div ref={content} className={styles.content} inert={wizard.busy}>
          <Dialog.Title
            render={<h2 ref={heading} className={styles.heading} tabIndex={-1} />}
          >
            {t(`web:exerciseLibrary.creator.steps.${wizard.step}.title`)}
          </Dialog.Title>
          {/* Board D: the two steps Buddy's proposal fills say where their values came from. */}
          {request.proposed !== undefined && wizard.step !== 'materials' && (
            <p className={styles.fromProposal}>
              {t(
                request.proposed.proposal === null
                  ? 'web:exerciseLibrary.creator.fromProposal.none'
                  : 'web:exerciseLibrary.creator.fromProposal.filled',
              )}
            </p>
          )}
          {wizard.step === 'basics' ? (
            <StepBasics wizard={wizard} />
          ) : wizard.step === 'body' ? (
            <StepBody wizard={wizard} />
          ) : (
            <StepMaterials wizard={wizard} source={request.source} />
          )}
        </div>
        <WizardFooter wizard={wizard} onCancel={onClose} />
      </div>
    </>
  );
}
